import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import { Input, Label, ModalHeader } from 'reactstrap';
import classnames from 'classnames';

import { ClearButton, PrimaryButton } from 'ui/Components';
import { makeId } from 'utils/selectors';

import styles from './RenderingEnvironment.css';
import {
  RENDERING_ENVIRONMENTS_FOR_PAGE,
  RENDERING_ENVIRONMENTS_FOR_COMPONENT,
  RENDERING_ENVIRONMENTS_FOR_EDIT,
  renderingEnvironmentsForPage,
  renderingEnvironmentsForComponent,
  responsiveContentOptions,
  defaultOptions,
  renderingEnvironmentsForEdit,
  homepageContentOptions,
  PAGE_TYPES,
  PAGE_TEMPLATES
} from '../const';
import { generatePageTemplateName, generateRenderingEnvironmentCaptions } from '../helper';

/**
 * This is a window which gives options to select rendering environments
 * for components and pages.
 * @param {object} renderingEnv - object containing pageType and pageTemplateName.
 * @param {object} onSelect - action to be dispatched on selection.
 * @param {object} onCancel - action to be dispatched on cancel.
 * @returns {React.Component} the rendering environment component.
 */
export default class RenderingEnvironment extends Component {
  static propTypes = {
    cancelText: propTypes.string,
    enterText: propTypes.string,
    environmentToRender: propTypes.string,
    onCancel: propTypes.func.isRequired,
    onEnter: propTypes.func.isRequired,
    renderingEnv: propTypes.shape({
      pageType: propTypes.string.isRequired,
      pageTemplateName: propTypes.string.isRequired
    })
  };

  static defaultProps = {
    cancelText: 'Nevermind',
    enterText: 'Continue',
    environmentToRender: RENDERING_ENVIRONMENTS_FOR_COMPONENT,
    renderingEnv: { ...renderingEnvironmentsForComponent[0].value }
  };

  constructor(props) {
    super(props);
    const { pageType, pageTemplateName } = props.renderingEnv;
    const split = pageTemplateName.split('-');
    this.state = {
      renderingEnvName:
        generateRenderingEnvironmentCaptions(pageType, pageTemplateName).pageType || '',
      renderingEnv: props.renderingEnv,
      pageTemplateName1:
        pageType === responsiveContentOptions.pageType
          ? split && split[0].trim()
          : responsiveContentOptions.options.responsiveContentOptions1[0].value,
      pageTemplateName2:
        pageType === responsiveContentOptions.pageType
          ? (split[1] || '').trim()
          : responsiveContentOptions.options.responsiveContentOptions2[0].value
    };
  }

  /**
   * Sets page type and/or page template Name in state
   * @param {object} e - event object
   */
  onPageTypeChange(e) {
    const selectedName = e.target.options[e.target.selectedIndex].text;
    const split = e.target.value.split(':');
    this.setState({
      renderingEnvName: selectedName,
      renderingEnv: {
        pageType: split[0],
        pageTemplateName:
          selectedName === PAGE_TYPES.CATEGORY_NAVIGATION_PRODUCT_RESULTS
            ? PAGE_TEMPLATES.CAT_NAV_PRODUCT_RESULTS
            : split[1]
      }
    });
  }

  /**
   * Sets the first half of page template name
   * @param {string} pageTemplateName1 - first half of page template name
   */
  onPageTemplateName1Changed(pageTemplateName1) {
    const { renderingEnv, pageTemplateName2 } = this.state;
    const pageTemplateName = generatePageTemplateName(pageTemplateName1, pageTemplateName2);
    this.setState({
      renderingEnv: {
        ...renderingEnv,
        pageTemplateName
      },
      pageTemplateName1
    });
  }

  /**
   * Sets the second half of page template name
   * @param {string} pageTemplateName2 - second half of page template name
   */
  onPageTemplateName2Changed(pageTemplateName2) {
    const { renderingEnv, pageTemplateName1 } = this.state;
    const pageTemplateName = generatePageTemplateName(pageTemplateName1, pageTemplateName2);
    this.setState({
      renderingEnv: {
        ...renderingEnv,
        pageTemplateName
      },
      pageTemplateName2
    });
  }

  /**
   * renders options for page template name if required
   * @returns {Component} returns options for page template name if required
   */
  renderPageTemplateOptions() {
    const { renderingEnvName } = this.state;
    switch (renderingEnvName) {
      case responsiveContentOptions.name:
      case homepageContentOptions.name:
      case defaultOptions.name:
        return this.renderResponsiveContentPageOptions();
      default:
        return <div />;
    }
  }

  /**
   * renders options for Responsive Content
   * @returns {Component} returns options for Responsive Content
   */
  renderResponsiveContentPageOptions() {
    const { pageTemplateName1, pageTemplateName2 } = this.state;
    const {
      responsiveContentOptions1,
      responsiveContentOptions2
    } = responsiveContentOptions.options;
    return (
      <div
        className={classnames(
          'd-flex mt-2 border border-secondary flex-row',
          styles.radioButton
        )}
      >
        <div className="d-flex border-right border-secondary w-50 ml-5 flex-column">
          {responsiveContentOptions1.map((option, index) => (
            <Label key={option.value}>
              <Input
                key={option.value}
                type="radio"
                checked={pageTemplateName1 === option.value}
                onChange={() => this.onPageTemplateName1Changed(option.value)}
                data-test-id={`responsiveContent1-radio-${index}`}
                name="responsiveContent1"
              />
              {option.name}
            </Label>
          ))}
        </div>
        <div className="d-flex w-50 ml-5 flex-column">
          {responsiveContentOptions2.map((option, index) => (
            <Label key={option.value}>
              <Input
                key={option.value}
                type="radio"
                checked={pageTemplateName2 === option.value}
                onChange={() => this.onPageTemplateName2Changed(option.value)}
                data-test-id={`responsiveContent2-radio-${index}`}
                name="responsiveContent2"
              />
              {option.name}
            </Label>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { renderingEnv } = this.state;
    const { onEnter, enterText, onCancel, cancelText, environmentToRender } = this.props;
    let renderingEnvironments;

    switch (environmentToRender) {
      case RENDERING_ENVIRONMENTS_FOR_PAGE:
        renderingEnvironments = renderingEnvironmentsForPage;
        break;
      case RENDERING_ENVIRONMENTS_FOR_COMPONENT:
        renderingEnvironments = renderingEnvironmentsForComponent;
        break;
      case RENDERING_ENVIRONMENTS_FOR_EDIT:
        renderingEnvironments = renderingEnvironmentsForEdit;
        break;
      default:
        renderingEnvironments = renderingEnvironmentsForPage;
        break;
    }

    return (
      <Fragment>
        <ModalHeader>Select a Rendering Environment</ModalHeader>
        <div className="d-flex flex-column mt-3">
          <div className={styles.wrapper}>
            <Input
              type="select"
              value={`${renderingEnv.pageType}:${renderingEnv.pageTemplateName}`}
              data-test-id="renderingEnv-Select"
              onChange={e => this.onPageTypeChange(e)}
            >
              {renderingEnvironments.map(env => (
                <option
                  key={env.name}
                  value={`${env.value.pageType}:${renderingEnv.pageTemplateName}`}
                  data-test-id={makeId({
                    ...env.value,
                    name: env.name
                  })}
                >
                  {env.name}
                </option>
              ))}
            </Input>
            {this.renderPageTemplateOptions()}
          </div>
          <div className="d-flex justify-content-end mt-auto">
            <ClearButton
              className="mr-2"
              data-test-id="addnewcomponent-nevermind-button"
              onClick={() => onCancel()}
            >
              {cancelText}
            </ClearButton>
            <PrimaryButton
              data-test-id="addnewcomponent-continue-button"
              onClick={() => onEnter(renderingEnv)}
            >
              {enterText}
            </PrimaryButton>
          </div>
        </div>
      </Fragment>
    );
  }
}
